import React, { useEffect, useRef, useState } from 'react';
import { ConditionRow, newCondition } from './segments/ConditionBuilder';
import { describeCondition } from './segments/conditionSummary';
import AutomationScenarioResults from './AutomationScenarioResults';
import AutomationSmsRouting from './AutomationSmsRouting';
import AutomationScenarioProgress from './AutomationScenarioProgress';

const input = 'automation-form-input w-full';
const secondary = 'automation-form-button';
const primary = 'automation-form-button automation-form-button--primary';
const group = () => ({ kind: 'group', operator: 'and', children: [] });
const editableGroup = (tree) => !tree?.kind ? group() : tree.kind === 'group' ? tree : { ...group(), children: [tree] };
const hasGeoCondition = (tree) => tree?.attribute === 'country' || (tree?.children || []).some(hasGeoCondition);

async function request(path, method, scenario, root = 'automation_scenario') {
  const response = await fetch(path, {
    method,
    headers: { 'Content-Type': 'application/json', Accept: 'application/json',
      'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content },
    body: scenario ? JSON.stringify({ [root]: scenario }) : undefined
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data.errors?.join('. ') || 'Request failed. Please try again.');
  return data;
}

export const Rules = ({ tree, onChange, library, segments, depth = 0, hideGeoOption = false, allowEvents = false, events = [] }) => {
  const children = tree.children || [];
  const attributes = (library?.groups || []).flatMap((g) => g.attributes);
  const update = (index, next) => onChange({ ...tree, children: children.map((c, i) => i === index ? next : c) });
  const remove = (index) => onChange({ ...tree, children: children.filter((_, i) => i !== index) });
  const add = (next) => onChange({ ...tree, children: [...children, next] });
  return <div className='border automation-form-border rounded-lg p-3 space-y-3'>
    <label className='flex items-center gap-2 text-sm automation-form-secondary'>Match
      <select aria-label='Condition group' value={tree.operator} className='border automation-form-border rounded px-2 py-1'
        onChange={(e) => onChange({ ...tree, operator: e.target.value })}>
        <option value='and'>AND: all conditions</option><option value='or'>OR: any condition</option>
      </select>
    </label>
    {children.map((node, index) => <div key={index}>
      {node.kind === 'group' ? <div>
        <div className='flex justify-end mb-1'><button type='button' className='text-xs automation-form-error'
          onClick={() => remove(index)}>Remove group</button></div>
        <Rules tree={node} library={library} segments={segments} depth={depth + 1} hideGeoOption={hideGeoOption} allowEvents={allowEvents} events={events} onChange={(next) => update(index, next)} />
      </div> : node.kind === 'event' ? <div className='flex gap-2 items-center'>
        <label>Event occurred
          <select aria-label='Exit event' className={input} value={node.event_type_id || ''}
            onChange={(event) => update(index, { ...node, event_type_id: Number(event.target.value) || null })}>
            <option value=''>Choose an event</option>
            {events.map((event) => <option key={event.id} value={event.id}>{event.name}</option>)}
          </select>
        </label>
        <button type='button' aria-label='Remove event condition' onClick={() => remove(index)}>×</button>
      </div> : node.kind === 'segment' ? <div className='flex gap-2 items-center'>
        <select aria-label='Segment membership' className='border automation-form-border rounded px-2 py-2 text-sm'
          value={node.operator} onChange={(e) => update(index, { ...node, operator: e.target.value })}>
          <option value='in'>IN</option><option value='not_in'>NOT IN</option>
          {allowEvents && <><option value='entered'>Entered during participation</option><option value='exited'>Exited during participation</option></>}
        </select>
        <select aria-label='Segment' required className={input} value={node.segment_id || ''}
          onChange={(e) => update(index, { ...node, segment_id: Number(e.target.value) })}>
          <option value=''>Choose a segment</option>
          {segments.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          {node.segment_id && !segments.some((s) => s.id === node.segment_id) &&
            <option value={node.segment_id}>Missing segment #{node.segment_id}</option>}
        </select>
        <button type='button' aria-label='Remove segment condition' className='automation-form-error' onClick={() => remove(index)}>×</button>
      </div> : (() => {
        const attribute = attributes.find((a) => a.id === node.attribute);
        if (!library) return <p className='text-xs automation-form-secondary'>Loading attributes…</p>;
        if (!attribute) return <div className='flex justify-between text-sm automation-form-error'>
          <span>Unavailable attribute: {node.attribute}</span><button type='button' onClick={() => remove(index)}>Remove</button>
        </div>;
        const extended = { ...attribute, operators: attribute.operators.includes('in')
          ? [...attribute.operators, 'not_in'] : attribute.operators };
        return <ConditionRow attribute={extended} condition={node} options={library.options || {}}
          unavailable={library.options_unavailable || {}} errors={[]} onChange={(next) => update(index, next)} onRemove={() => remove(index)} />;
      })()}
    </div>)}
    <div className='flex flex-wrap gap-2'>
      <select aria-label='Add attribute' className='border automation-form-border rounded px-2 py-2 text-sm flex-1'
        value='' disabled={!library} onChange={(e) => {
          const attribute = attributes.find((a) => a.id === e.target.value);
          if (attribute) add({ kind: 'attribute', ...newCondition(attribute) });
        }}>
        <option value=''>{library ? 'Add attribute' : 'Loading attributes…'}</option>
        {(library?.groups || []).map((g) => <optgroup key={g.group} label={g.label}>
          {g.attributes.filter((a) => !hideGeoOption || a.id !== 'country').map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </optgroup>)}
      </select>
      <button type='button' className={secondary} onClick={() => add({ kind: 'segment', operator: 'in', segment_id: null })}>Add segment</button>
      {allowEvents && <button type='button' className={secondary} onClick={() => add({ kind: 'event', event_type_id: null })}>Add event</button>}
      {depth < 3 && <button type='button' className={secondary} onClick={() => add(group())}>Add group</button>}
    </div>
  </div>;
};

export const describeTree = (tree, attributes, segments, events = []) => {
  if (!tree?.kind) return 'None';
  if (tree.kind === 'group') return tree.children.length ? `(${tree.children.map((c) => describeTree(c, attributes, segments, events)).join(` ${tree.operator.toUpperCase()} `)})` : 'Add conditions';
  if (tree.kind === 'event') return `Event: ${events.find((event) => event.id === Number(tree.event_type_id))?.name || 'Choose an event'}`;
  if (tree.kind === 'segment') return `${({in: 'IN', not_in: 'NOT IN', entered: 'Entered', exited: 'Exited'})[tree.operator]} ${segments.find((s) => s.id === Number(tree.segment_id))?.name || `segment #${tree.segment_id}`}`;
  return `${tree.operator === 'not_in' ? 'NOT IN ' : ''}${describeCondition(tree, attributes[tree.attribute]) || tree.attribute}`;
};

export const ScenarioForm = ({ initial, events, segments, smsTemplates, emailTemplates, emailSenders, libraries, libraryError, onSaved, onClose, chainMode = false }) => {
  const dialogRef = useRef(null);
  const [name, setName] = useState(initial?.name || '');
  const [triggerKind, setTriggerKind] = useState(initial?.trigger_kind || 'event');
  const [eventId, setEventId] = useState(initial?.event_type_id || '');
  const [trigger, setTrigger] = useState(editableGroup(initial?.trigger_conditions));
  const [withDelay, setWithDelay] = useState(Boolean(initial?.delay_value));
  const [delay, setDelay] = useState(initial?.delay_value || 1);
  const [unit, setUnit] = useState(initial?.delay_unit || 'days');
  const [withFilter, setWithFilter] = useState(Boolean(initial?.filter_conditions?.kind));
  const [filter, setFilter] = useState(editableGroup(initial?.filter_conditions));
  const [channel, setChannel] = useState(initial?.channel || 'sms');
  const [templateId, setTemplateId] = useState(initial?.sms_template_id || initial?.email_template_id || '');
  const [emailSenderId, setEmailSenderId] = useState(initial?.email_sender_id || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const formState = useRef({ busy, onClose });
  formState.current = { busy, onClose };
  useEffect(() => {
    const previous = document.activeElement;
    const handleKey = (event) => {
      if (event.key === 'Escape') { if (!formState.current.busy) formState.current.onClose(); return; }
      if (event.key !== 'Tab') return;
      const elements = Array.from(dialogRef.current.querySelectorAll('button, input, select, a[href], [tabindex="0"]'))
        .filter((element) => !element.disabled && !element.closest('fieldset[disabled]') && element.getClientRects().length);
      const first = elements[0], last = elements[elements.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previous?.focus(); };
  }, []);
  const templates = channel === 'sms' ? smsTemplates : emailTemplates;
  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try {
      const base = chainMode ? '/automation-chains' : '/event-scenarios';
      const saved = await request(initial ? `${base}/${initial.id}` : base, initial ? 'PATCH' : 'POST', {
        name, trigger_kind: triggerKind, event_type_id: triggerKind === 'event' ? eventId || null : null,
        trigger_conditions: triggerKind === 'conditions' && trigger.children.length ? trigger : {},
        delay_value: withDelay ? delay : null, delay_unit: withDelay ? unit : null,
        filter_conditions: withFilter ? filter : {}, channel,
        sms_template_id: channel === 'sms' ? templateId || null : null,
        email_template_id: channel === 'email' ? templateId || null : null,
        email_sender_id: channel === 'email' ? emailSenderId || null : null
      }, chainMode ? 'automation_chain' : 'automation_scenario');
      onSaved(saved);
    } catch (err) { setError(err.message); setBusy(false); }
  };
  return <div className='automation-form fixed inset-0 z-50 flex items-center justify-center p-4'>
    <section ref={dialogRef} role='dialog' aria-modal='true' aria-labelledby='scenario-title'
      className='automation-form-surface rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
      <div className='flex justify-between items-center px-5 py-4 border-b automation-form-border'>
        <h2 id='scenario-title' className='font-semibold'>{chainMode ? (initial ? 'Edit automation chain' : 'New automation chain') : (initial ? 'Edit scenario' : 'New scenario')}</h2>
        <button type='button' aria-label='Close scenario' disabled={busy} onClick={onClose}>×</button>
      </div>
      <form onSubmit={submit} className='p-5 space-y-5'>
        <fieldset disabled={busy} className='space-y-5'>
          <label className='block text-sm font-medium'>Name
            <input autoFocus required value={name} onChange={(e) => setName(e.target.value)} className={`${input} mt-1`} />
          </label>
          <section className='space-y-3'>
            <h3 className='font-semibold text-sm automation-form-text'>1. Trigger</h3>
            <select aria-label='Trigger type' className={input} value={triggerKind} onChange={(e) => setTriggerKind(e.target.value)}>
              <option value='event'>Player event</option><option value='conditions'>Player conditions</option>
            </select>
            {triggerKind === 'event' ? <select aria-label='Trigger event' required={!chainMode || initial?.status && initial.status !== 'draft'} className={input} value={eventId}
              onChange={(e) => setEventId(Number(e.target.value))}>
              <option value=''>Choose an event</option>
              {events.map((event) => <option key={event.id} value={event.id}>{event.name}{event.active ? '' : ' (Inactive)'}</option>)}
            </select> : <Rules tree={trigger} onChange={setTrigger} library={libraries.trigger} segments={segments} hideGeoOption={hasGeoCondition(trigger)} />}
          </section>
          <section className='space-y-3'>
            <h3 className='font-semibold text-sm automation-form-text'>2. Time Delay</h3>
            <label className='flex gap-2 text-sm'><input type='checkbox' checked={withDelay} onChange={(e) => setWithDelay(e.target.checked)} />Wait after Trigger</label>
            {withDelay && <div className='grid grid-cols-2 gap-2'>
              <input aria-label='Delay amount' type='number' required min='1' max='2147483647' step='1' className={input} value={delay} onChange={(e) => setDelay(e.target.value)} />
              <select aria-label='Delay unit' className={input} value={unit} onChange={(e) => setUnit(e.target.value)}>
                <option value='minutes'>Minutes</option><option value='hours'>Hours</option><option value='days'>Days</option>
              </select>
            </div>}
          </section>
          <section className='space-y-3'>
            <h3 className='font-semibold text-sm automation-form-text'>3. Filter</h3>
            <label className='flex gap-2 text-sm'><input type='checkbox' checked={withFilter} onChange={(e) => setWithFilter(e.target.checked)} />Check player conditions before sending</label>
            {withFilter && <Rules tree={filter} onChange={setFilter} library={libraries.filter} segments={segments} />}
          </section>
          <section className='space-y-3'>
            <h3 className='font-semibold text-sm automation-form-text'>4. Action</h3>
            <select aria-label='Action channel' className={input} value={channel} onChange={(e) => { setChannel(e.target.value); setTemplateId(''); }}>
              <option value='sms'>Send SMS</option><option value='email'>Send Email</option>
            </select>
            {channel === 'email' && <label className='block text-sm'>From
              <select aria-label='Email sender' className={`${input} mt-1`} value={emailSenderId}
                onChange={(e) => setEmailSenderId(Number(e.target.value))}>
                <option value=''>Choose an email sender</option>
                {emailSenders.map((sender) => <option key={sender.id} value={sender.id} disabled={!sender.sendable}>
                  {sender.name}{sender.sendable ? '' : ' (Unavailable)'}
                </option>)}
              </select>
            </label>}
            {channel === 'sms' && <AutomationSmsRouting />}
            <select aria-label='Message template' className={input} required={!chainMode || initial?.status && initial.status !== 'draft'} value={templateId} onChange={(e) => setTemplateId(Number(e.target.value))}>
              <option value=''>Choose a template</option>
              {templates.map((t) => <option key={t.id} value={t.id} disabled={channel === 'email' && !t.has_unsubscribe}>
                {t.name}{channel === 'email' && !t.has_unsubscribe ? ' (Unsubscribe link missing)' : ''}
              </option>)}
            </select>
            {templateId && <p className='text-sm automation-form-secondary whitespace-pre-wrap break-words'>
              {channel === 'sms' ? templates.find((t) => t.id === Number(templateId))?.text : templates.find((t) => t.id === Number(templateId))?.subject}
            </p>}
          </section>
        </fieldset>
        <p className='text-xs automation-form-secondary'>{chainMode ? (initial && initial.status !== 'draft' ? 'Changes apply to waiting players. Completed actions will not run again.' : 'Save a draft at any time. Configure the Trigger and Action before starting.') : (initial ? 'Changes apply to all waiting players.' : 'Saved as Paused. Activate the scenario from the list when ready.')}</p>
        {libraryError && <p role='alert' className='text-sm automation-form-warning'>{libraryError}</p>}
        {error && <p role='alert' className='text-sm automation-form-error'>{error}</p>}
        <div className='flex justify-end gap-2'>
          <button type='button' disabled={busy} onClick={onClose} className={secondary}>Cancel</button>
          <button type='submit' disabled={busy} className={primary}>{busy ? 'Saving…' : chainMode ? 'Save chain' : 'Save scenario'}</button>
        </div>
      </form>
    </section>
  </div>;
};

const AutomationScenarios = ({ initialScenarios = [], events, segments = [], smsTemplates = [], emailTemplates = [], attributeGroups = [], emailSenders = [] }) => {
  const [scenarios, setScenarios] = useState(initialScenarios);
  const [modal, setModal] = useState(null);
  const [libraries, setLibraries] = useState({});
  const [libraryError, setLibraryError] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [progressById, setProgressById] = useState({});
  const [progressError, setProgressError] = useState('');
  useEffect(() => {
    if (busy) return undefined;
    let controller;
    const refresh = async () => {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      try {
        const response = await fetch('/event-scenarios/progress', {headers: {Accept: 'application/json'}, signal: current.signal});
        if (!response.ok) throw new Error();
        const data = await response.json();
        if (current.signal.aborted) return;
        const byId = Object.fromEntries(data.map((item) => [item.id, item]));
        setProgressById(byId);
        setScenarios((list) => list.map((s) => byId[s.id] ? {...s, status: byId[s.id].scenario_status} : s));
        setProgressError('');
      } catch (err) {
        if (err.name !== 'AbortError' && !current.signal.aborted) setProgressError('Progress could not be refreshed. Displayed values may be out of date.');
      }
    };
    refresh();
    const interval = setInterval(refresh, 5000);
    return () => { clearInterval(interval); controller?.abort(); };
  }, [busy]);
  useEffect(() => {
    if (!modal || !['new', 'edit'].includes(modal.mode) || (libraries.trigger && libraries.filter)) return undefined;
    const abort = new AbortController();
    Promise.all(['trigger', 'filter'].map((purpose) => fetch(`/api/v1/attributes?for=automation_${purpose}`, {
      headers: { Accept: 'application/json' }, signal: abort.signal
    }).then((response) => { if (!response.ok) throw new Error(); return response.json(); })))
      .then(([trigger, filter]) => { setLibraries({ trigger, filter }); setLibraryError(''); })
      .catch((err) => { if (err.name !== 'AbortError') setLibraryError('Could not load attributes. Close the form and try again.'); });
    return () => abort.abort();
  }, [modal, libraries]);
  const attributes = {};
  attributeGroups.forEach((g) => g.attributes.forEach((a) => { attributes[a.id] = a; }));
  const saved = (scenario) => {
    setScenarios((list) => list.some((s) => s.id === scenario.id)
      ? list.map((s) => s.id === scenario.id ? scenario : s) : [scenario, ...list]);
    setProgressById((previous) => ({...previous, [scenario.id]: {
      ...previous[scenario.id], scan: scenario.scan_progress || {}, scenario_status: scenario.status,
      state: scenario.status === 'paused' ? 'paused' :
        (scenario.trigger_kind === 'event' ? 'listening' : scenario.scan_progress?.state || 'queued')
    }}));
    setModal(null);
  };
  const remove = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      await request(`/event-scenarios/${modal.scenario.id}`, 'DELETE');
      setScenarios((list) => list.filter((s) => s.id !== modal.scenario.id)); setModal(null);
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  const changeStatus = async (scenario) => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      saved(await request(`/event-scenarios/${scenario.id}/${scenario.status === 'active' ? 'pause' : 'activate'}`, 'POST'));
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };
  return <section className='mt-8'>
    <div className='flex justify-between items-center gap-4 mb-4'>
      <div><h2 className='text-lg font-semibold text-slate-900'>Automatic scenarios</h2>
        <p className='text-sm text-slate-500 mt-1'>Trigger → Time Delay → Filter → one SMS or Email.</p></div>
      <button className={primary} onClick={() => setModal({ mode: 'new' })}>New scenario</button>
    </div>
    {error && !modal && <p role='alert' className='text-sm text-red-600 mb-3'>{error}</p>}
    <p role={progressError ? 'alert' : undefined} className={`automation-progress-note${progressError ? ' automation-progress-note--error' : ''}`}>
      {progressError || 'Progress updates every 5 seconds. Open Results for check times and all counters.'}
    </p>
    <div className='bg-white border border-slate-200 rounded-xl overflow-x-auto'>
      <table className='w-full text-sm text-left'>
        <thead className='bg-slate-50 text-slate-500'><tr>{['Name', 'Progress', 'Trigger', 'Time Delay', 'Filter', 'Action', 'Channel', 'Template', 'Status', 'Actions'].map((label) =>
          <th key={label} scope='col' className='px-4 py-3 font-medium'>{label}</th>)}</tr></thead>
        <tbody className='divide-y divide-slate-100'>
          {scenarios.map((s) => <tr key={s.id}>
            <td className='px-4 py-3'><button className='text-blue-600 text-left' onClick={() => setModal({ mode: 'edit', scenario: s })}>{s.name}</button></td>
            <td className='px-4 py-3'><AutomationScenarioProgress progress={progressById[s.id]} /></td>
            <td className='px-4 py-3'>{s.trigger_kind === 'event' ? events.find((e) => e.id === s.event_type_id)?.name || 'Missing event' : describeTree(s.trigger_conditions, attributes, segments)}</td>
            <td className='px-4 py-3'>{s.delay_value ? `${s.delay_value} ${s.delay_unit}` : 'None'}</td>
            <td className='px-4 py-3 max-w-xs break-words'>{describeTree(s.filter_conditions, attributes, segments)}</td>
            <td className='px-4 py-3'>Send {s.channel === 'sms' ? 'SMS' : 'Email'}</td>
            <td className='px-4 py-3'>{s.channel === 'sms' ? 'SMS' : 'Email'}</td>
            <td className='px-4 py-3 text-slate-500'>{(s.channel === 'sms' ? smsTemplates : emailTemplates).find((t) => t.id === (s.sms_template_id || s.email_template_id))?.name || 'Missing template'}</td>
            <td className='px-4 py-3'><span className={`px-2 py-1 rounded text-xs ${s.status === 'active' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>
              {s.status === 'active' ? 'Active' : 'Paused'}
            </span></td>
            <td className='px-4 py-3'><div className='flex flex-wrap gap-3'>
              <button disabled={busy} className={`${s.status === 'active' ? 'text-amber-700' : 'text-emerald-700'} disabled:opacity-50`}
                onClick={() => changeStatus(s)}>{s.status === 'active' ? 'Pause' : 'Activate'}</button>
              <button className='text-blue-600' onClick={() => setModal({ mode: 'results', scenario: s })}>Results</button>
              <button className='text-blue-600' onClick={() => setModal({ mode: 'edit', scenario: s })}>Edit</button>
              <button className='text-red-600' onClick={() => { setError(''); setModal({ mode: 'delete', scenario: s }); }}>Delete</button>
            </div></td>
          </tr>)}
          {!scenarios.length && <tr><td colSpan={10} className='px-4 py-10 text-center text-slate-500'>No scenarios yet.</td></tr>}
        </tbody>
      </table>
    </div>
    {['new', 'edit'].includes(modal?.mode) && <ScenarioForm initial={modal.scenario} events={events} segments={segments}
      smsTemplates={smsTemplates} emailTemplates={emailTemplates} emailSenders={emailSenders} libraries={libraries} libraryError={libraryError} onSaved={saved} onClose={() => setModal(null)} />}
    {modal?.mode === 'results' && <AutomationScenarioResults scenario={modal.scenario} onClose={() => setModal(null)} />}
    {modal?.mode === 'delete' && <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4'>
      <section role='dialog' aria-modal='true' aria-labelledby='delete-scenario-title' className='bg-white rounded-xl shadow-xl w-full max-w-md p-5'>
        <h2 id='delete-scenario-title' className='font-semibold mb-3'>Delete scenario</h2>
        <p className='text-sm'>Delete “{modal.scenario.name}”? This cannot be undone.</p>
        {error && <p role='alert' className='text-sm text-red-600 mt-3'>{error}</p>}
        <div className='flex justify-end gap-2 mt-5'>
          <button className={secondary} disabled={busy} onClick={() => setModal(null)}>Cancel</button>
          <button className='px-3 py-2 text-sm rounded-lg bg-red-600 text-white disabled:opacity-50' disabled={busy} onClick={remove}>Delete scenario</button>
        </div>
      </section>
    </div>}
  </section>;
};

export default AutomationScenarios;
